import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PawPrint, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { WigglezMark } from "@/components/WigglezMark";
import { fbTrack } from "@/lib/fbpixel";

const HERO = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/1bfbfaf70c5d64584eccfc7f46014b872f6f033fbdaa0b45f4e5b30fb57bf5f0.jpeg";
const CITY_LABEL = { vegas: "702 · VEGAS", miami: "305 · MIAMI", nyc: "917 · NYC/PR" };
const CITY_FILTERS = [
  { key: "all", label: "All Cities" },
  { key: "vegas", label: "702 Vegas" },
  { key: "miami", label: "305 Miami" },
  { key: "nyc", label: "917 NYC/PR" },
];

export default function Sets() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("all");
  const [email, setEmail] = useState("");
  const { addItem } = useCart();

  useEffect(() => {
    api.get("/products").then(({ data }) => setAll(data)).finally(() => setLoading(false));
  }, []);

  const sets = useMemo(
    () => all.filter((p) => (p.tags || []).includes("set-combo")).filter((p) => (city === "all" ? true : p.city === city)),
    [all, city]
  );

  const shopLook = (p) => {
    addItem(p, 1, { fit: "full-set" });
    toast.success(`${p.name} — full fit added to bag 🐾`);
  };

  const alertSignup = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/subscribe", { email, source: "drop-alerts" });
      fbTrack("Lead", { content_name: "drop-alerts" });
      toast.success("You're on the list — drop alerts incoming 🐾");
      setEmail("");
    } catch { toast.error("Couldn't sign you up — try again"); }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="sets-page">
      {/* Hero */}
      <div className="relative h-[62vh] min-h-[440px] w-full overflow-hidden">
        <img src={HERO} alt="The Sets" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-black/50" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-12">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="overline text-primary mb-3">The Sets · Editorial Looks</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl">
            Full <span className="neon-text">fits.</span> Full <span style={{ color: "#ccff00" }}>hearts.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.12 }} className="text-muted-foreground mt-4 max-w-xl">
            Head-to-toe city looks with gold-heart pieces and the pack. 702 for King. 305 for Smiley. 917 for Beats. Rep your city.
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.18 }} className="mt-3 text-sm font-bold uppercase tracking-wide" style={{ color: "rgba(204,255,0,0.9)" }}>
            The Doggfather · Mr. Wigglez — and Mrs. Wigglez? Any pit who wiggles their butt when they're happy.
          </motion.p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        {/* Drop Alerts */}
        <div className="mb-12 rounded-2xl border p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-6" style={{ borderColor: "rgba(204,255,0,0.4)" }} data-testid="drop-alerts">
          <div className="flex-1">
            <p className="overline inline-flex items-center gap-2 mb-2" style={{ color: "#ccff00" }}><BellRinging weight="fill" size={16} /> Drop Alerts</p>
            <h2 className="font-display font-black uppercase text-2xl sm:text-3xl leading-tight">Be first when a set drops.</h2>
            <p className="text-sm text-muted-foreground mt-2">Limited runs. When they're gone, they're gone. Get the alert before the city does.</p>
          </div>
          <form onSubmit={alertSignup} className="flex w-full md:w-auto gap-3" data-testid="drop-alerts-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
              className="flex-1 md:w-64 bg-transparent border border-white/15 rounded-full px-5 py-3 outline-none focus:border-[#ccff00] transition-colors" data-testid="drop-alerts-email" />
            <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-black uppercase tracking-wide text-black" style={{ background: "#ccff00" }} data-testid="drop-alerts-submit">
              Alert me
            </button>
          </form>
        </div>

        {/* City filters */}
        <div className="flex gap-2 mb-10 overflow-x-auto no-scrollbar" data-testid="sets-city-filters">
          {CITY_FILTERS.map((f) => (
            <button key={f.key} onClick={() => setCity(f.key)}
              className={`overline whitespace-nowrap px-4 py-2 rounded-full border transition-colors ${city === f.key ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-muted-foreground hover:text-white"}`}
              data-testid={`sets-city-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-8">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-zinc-900 animate-pulse" />)}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8" data-testid="sets-grid">
            {sets.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                className="group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0a] hover:border-[#ccff00]/60 transition-colors" data-testid={`set-card-${p.id}`}>
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={(p.images || [])[0]} alt={p.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />
                  <span className="absolute top-4 left-4 overline px-3 py-1.5 rounded-full bg-black/60 backdrop-blur text-primary border border-primary/40">{CITY_LABEL[p.city] || "WIGGLEZ"}</span>
                  {p.exclusivity && (
                    <span className="absolute top-4 right-4 overline px-3 py-1 rounded-full text-black font-semibold" style={{ background: "linear-gradient(90deg,#fbbf24,#d97706)" }}>{p.exclusivity}</span>
                  )}
                  <div className="absolute bottom-3 left-3 px-2 py-1 rounded-md bg-black/55 backdrop-blur-sm border border-white/10"><WigglezMark size="text-[11px]" /></div>
                </div>
                <div className="p-6">
                  <p className="overline text-muted-foreground tracking-[0.2em] mb-1">{p.slogan}</p>
                  <h3 className="font-display font-black uppercase text-2xl leading-tight">{p.name}</h3>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.description}</p>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <span className="text-xl font-modern">${p.price.toLocaleString()}</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => shopLook(p)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] font-black uppercase text-black text-sm hover:opacity-90 transition-opacity" style={{ background: "#ccff00" }} data-testid={`set-addlook-${p.id}`}>
                        <PawPrint weight="fill" size={15} /> Add fit
                      </button>
                      <Link to={`/product/${p.id}`} className="overline px-3 py-2.5 rounded-[10px] border border-white/15 text-muted-foreground hover:text-white transition-colors" data-testid={`set-shop-${p.id}`}>View</Link>
                    </div>
                  </div>
                  {(p.images || [])[1] && (
                    <img src={p.images[1]} alt={`${p.name} detail`} className="mt-4 h-28 w-full object-cover rounded-lg border border-white/10" data-testid={`set-detail-${p.id}`} />
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
